import * as React from 'react'

/**
 * Zamiennik `react-flexview` (3.0.2, ostatnie wydanie 2018).
 *
 * Renderuje ten sam DOM i te same wyliczenia co oryginał, tylko style idą inline
 * zamiast przez klasy z `flexView.css` - dzięki temu nie trzeba wciągać arkusza
 * z paczki. Klasa `react-flex-view` zostaje, bo po niej łatwo poznać element
 * w devtoolsach (żaden nasz arkusz jej nie używa).
 *
 * Odwzorowane 1:1 z oryginału:
 *   - `grow`: liczba -> liczba, `true` -> 1, brak -> 0
 *   - `shrink`: liczba -> liczba, `true` -> 1, `false` -> 0,
 *      brak przy ustawionym `basis` (innym niż "auto") -> 0, inaczej -> 1
 *   - `basis`: liczba (lub tekst wyglądający na liczbę) dostaje sufiks `px`
 *   - w wierszu `vAlignContent` steruje `align-items`, a `hAlignContent`
 *     `justify-content`; w kolumnie odwrotnie
 *   - `min-width: 0` i `min-height: 0` - bez nich dzieci nie kurczą się poniżej
 *     swojej zawartości
 */
export type FlexViewProps = Omit<
  React.HTMLProps<HTMLDivElement>,
  'ref' | 'height' | 'width' | 'wrap'
> & {
  children?: React.ReactNode
  /** flex-direction: column */
  column?: boolean
  /** wyrównanie w pionie */
  vAlignContent?: 'top' | 'center' | 'bottom'
  /** wyrównanie w poziomie */
  hAlignContent?: 'left' | 'center' | 'right'
  marginLeft?: string | number
  marginTop?: string | number
  marginRight?: string | number
  marginBottom?: string | number
  grow?: boolean | number
  shrink?: boolean | number
  basis?: string | number
  wrap?: boolean
  height?: string | number
  width?: string | number
  className?: string
  style?: React.CSSProperties
}

const V_ALIGN = { top: 'flex-start', center: 'center', bottom: 'flex-end' } as const
const H_ALIGN = { left: 'flex-start', center: 'center', right: 'flex-end' } as const

const resolveGrow = (grow: FlexViewProps['grow']) => {
  if (typeof grow === 'number') return grow
  return grow ? 1 : 0
}

const resolveShrink = (
  shrink: FlexViewProps['shrink'],
  basis: FlexViewProps['basis'],
) => {
  if (typeof shrink === 'number') return shrink
  if (shrink === true) return 1
  if (shrink === false) return 0
  if (basis && basis !== 'auto') return 0
  return 1
}

const resolveBasis = (basis: FlexViewProps['basis']) => {
  if (!basis) return 'auto'
  const numeric = typeof basis === 'number' || String(parseInt(basis, 10)) === basis
  return `${basis}${numeric ? 'px' : ''}`
}

const FlexView: React.FC<FlexViewProps> = ({
  children,
  column,
  vAlignContent,
  hAlignContent,
  marginLeft,
  marginTop,
  marginRight,
  marginBottom,
  grow,
  shrink,
  basis,
  wrap,
  height,
  width,
  className,
  style,
  ...rest
}) => {
  const alignSource = column ? hAlignContent : vAlignContent
  const justifySource = column ? vAlignContent : hAlignContent

  const alignItems = column
    ? alignSource && H_ALIGN[alignSource as keyof typeof H_ALIGN]
    : alignSource && V_ALIGN[alignSource as keyof typeof V_ALIGN]
  const justifyContent = column
    ? justifySource && V_ALIGN[justifySource as keyof typeof V_ALIGN]
    : justifySource && H_ALIGN[justifySource as keyof typeof H_ALIGN]

  const flex = `${resolveGrow(grow)} ${resolveShrink(shrink, basis)} ${resolveBasis(basis)}`

  return (
    <div
      className={['react-flex-view', className].filter(Boolean).join(' ')}
      style={{
        display: 'flex',
        minWidth: 0,
        minHeight: 0,
        flexDirection: column ? 'column' : 'row',
        flexWrap: wrap ? 'wrap' : 'nowrap',
        alignItems: alignItems ?? 'stretch',
        ...(justifyContent ? { justifyContent } : {}),
        flex,
        width,
        height,
        marginLeft,
        marginTop,
        marginRight,
        marginBottom,
        ...style,
      }}
      {...(rest as React.HTMLAttributes<HTMLDivElement>)}
    >
      {children}
    </div>
  )
}

export default FlexView
